import React from 'react';
import { Box, Typography, Chip, Stack, ToggleButtonGroup, ToggleButton, IconButton, Tooltip } from '@mui/material';
import { FileUpload, KeyboardDoubleArrowDown } from '@mui/icons-material';

interface PhotoGridHeaderProps {
  photosCount: number;
  uploadingCount: number;
  failedCount: number;
  photosWithoutCaptionsCount: number;
  hasUnsavedChanges: boolean;
  isSaving: boolean;
  photosPerRow: number;
  onSaveChanges?: () => void;
  onCancelChanges: () => void;
  onPhotosPerRowChange: (photosPerRow: number) => void;
  onScrollToBottom?: () => void;
  onUploadClick?: () => void;
  isReadOnly?: boolean;
}

const PhotoGridHeader: React.FC<PhotoGridHeaderProps> = ({
  photosCount,
  uploadingCount,
  failedCount,
  photosWithoutCaptionsCount,
  hasUnsavedChanges,
  isSaving,
  photosPerRow,
  onSaveChanges,
  onCancelChanges,
  onPhotosPerRowChange,
  onScrollToBottom,
  onUploadClick,
  isReadOnly = false,
}) => {
  const hasUploadingPhotos = uploadingCount > 0;
  const hasFailedPhotos = failedCount > 0;

  return (
    <Box sx={styles.container}>
      <Box>
        <Typography variant='h6' gutterBottom>
          Photos ({photosCount}){hasUploadingPhotos && ` + ${uploadingCount} uploading`}
          {hasFailedPhotos && ` + ${failedCount} failed`}
        </Typography>
        {!isReadOnly && photosWithoutCaptionsCount > 0 && (
          <Chip label={`${photosWithoutCaptionsCount} photo(s) without captions`} color='warning' size='small' sx={styles.warningChip} />
        )}
        {hasUnsavedChanges && <Chip label='Unsaved changes' color='info' size='small' sx={styles.unsavedChip} />}
      </Box>

      <Stack direction='row' spacing={2} alignItems='center'>
        <Stack direction='row' marginTop={'auto !important'}>
          {onUploadClick && !isReadOnly && (
            <Tooltip title='Upload photos'>
              <IconButton onClick={onUploadClick} aria-label='Upload photos'>
                <FileUpload sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
          )}

          {onScrollToBottom && (
            <Tooltip title='Scroll to bottom'>
              <IconButton onClick={onScrollToBottom} aria-label='Scroll to bottom'>
                <KeyboardDoubleArrowDown sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
          )}
        </Stack>

        {/* Photos per row selector */}
        <Box sx={styles.gridDensitySelector}>
          <Typography variant='body2' sx={styles.labelText}>
            Photos per Row
          </Typography>
          <ToggleButtonGroup
            value={photosPerRow}
            exclusive
            onChange={(event, newPhotosPerRow) => {
              if (newPhotosPerRow !== null) {
                onPhotosPerRowChange(newPhotosPerRow);
              }
            }}
            aria-label='Photos per row'
            size='medium'
            sx={styles.toggleButtonGroup}
          >
            <ToggleButton value={2} aria-label='2 photos per row'>
              2
            </ToggleButton>
            <ToggleButton value={3} aria-label='3 photos per row'>
              3
            </ToggleButton>
            <ToggleButton value={4} aria-label='4 photos per row'>
              4
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Stack>
    </Box>
  );
};

const styles = {
  container: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    mb: 2,
  },
  warningChip: {
    mt: 1,
    backgroundColor: 'warning.light',
    color: 'text.primary',
  },
  unsavedChip: {
    mt: 1,
    ml: 1,
  },
  toggleButtonGroup: {
    height: '2.5rem',
  },
  gridDensitySelector: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  labelText: {
    fontSize: '0.8rem',
    mb: 0.5,
  },
};

export default PhotoGridHeader;
